import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { TrashIcon, FloppyDiskIcon, ArchiveIcon, ArrowCounterClockwiseIcon, BellRingingIcon, ArrowRightIcon } from '@phosphor-icons/react';
import { ThresholdFields } from '@/components/Threshold';

const HAZARD_FLAGS = [
  { key: 'forklift_on_site', label: 'Forklift / PIT on site', detail: 'Adds Forklift Program + Operator Certs + Pre-shift Inspections + Training Records' },
  { key: 'equipment_with_moving_parts', label: 'Equipment with moving parts / hazardous energy', detail: 'Adds LOTO Program + Machine Procedures + Machine Guarding + Annual LOTO Inspection + Training Records' },
  { key: 'noise_exposure', label: 'Noise exposure ≥85 dBA 8-hr TWA', detail: 'Adds Hearing Conservation Program + Annual Training' },
  { key: 'respirator_use', label: 'Respirators required or voluntarily used', detail: 'Adds Respiratory Protection Program + Training' },
  { key: 'confined_space', label: 'Permit-required confined spaces on site', detail: 'Adds Confined Space Program (29 CFR 1910.146)' },
  { key: 'chemical_exposure', label: 'Regulated substance exposure (lead, silica, hex chrome)', detail: 'Adds substance-specific written exposure control plans' },
  { key: 'first_aid_responders_on_site', label: 'On-site first responders / first aid program', detail: 'Adds Bloodborne Pathogen Exposure Control Plan (29 CFR 1910.1030)' },
  { key: 'heat_exposure', label: 'Heat exposure (ambient or process heat)', detail: 'Adds Heat Stress Prevention Plan. Auto-on for manufacturing environments.' },
];

function HazardFlag({ hazard, checked, onChange }) {
  return (
    <label
      className="flex items-start gap-2 p-2 border-2 rounded-sm cursor-pointer transition-colors"
      style={{
        borderColor: checked ? 'var(--gl-navy)' : '#d1d5db',
        background: checked ? '#eff4fb' : '#fff',
      }}
    >
      <input
        type="checkbox"
        className="mt-0.5 w-4 h-4 flex-shrink-0"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={`setup-hazard-${hazard.key}`}
      />
      <div className="min-w-0">
        <div
          className="font-bold uppercase tracking-wider text-xs"
          style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}
        >
          {hazard.label}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: 'var(--gl-muted)' }}>
          {hazard.detail}
        </div>
      </div>
    </label>
  );
}

export default function SetupTab({ eng, refresh }) {
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: eng.business_name || '',
    contact_name: eng.contact_name || '',
    contact_email: eng.contact_email || '',
    contact_phone: eng.contact_phone || '',
    site_address: eng.site_address || '',
    site_address_note: eng.site_address_note || '',
    industry: eng.industry || '',
    visit_date: eng.visit_date || '',
    notes: eng.notes || '',
    has_fleet: !!eng.has_fleet,
    employee_count: eng.employee_count || '',
    industry_type: eng.industry_type || '',
    // GL-SPEC-APP-004 Phase A hazard flags
    forklift_on_site: !!eng.forklift_on_site,
    equipment_with_moving_parts: !!eng.equipment_with_moving_parts,
    noise_exposure: !!eng.noise_exposure,
    respirator_use: !!eng.respirator_use,
    confined_space: !!eng.confined_space,
    chemical_exposure: !!eng.chemical_exposure,
    first_aid_responders_on_site: !!eng.first_aid_responders_on_site,
    heat_exposure: eng.heat_exposure ?? (eng.industry_type === 'manufacturing'),
  });
  const [busy, setBusy] = useState(false);
  const [reminders, setReminders] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState(eng.contact_email || '');

  useEffect(() => {
    if (eng.status === 'delivered' || eng.status === 'archived') {
      api.get(`/engagements/${eng.id}/reminders`).then((r) => setReminders(r.data)).catch(() => setReminders([]));
    } else {
      setReminders([]);
    }
  }, [eng.id, eng.status]);

  async function toggleAllNotify(on) {
    try {
      const fd = new FormData();
      fd.append('notify_client', on ? 'true' : 'false');
      if (notifyEmail) fd.append('client_email', notifyEmail);
      const { data } = await api.patch(`/engagements/${eng.id}/reminders/notify_client`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${data.updated} reminders updated`);
      const rs = await api.get(`/engagements/${eng.id}/reminders`);
      setReminders(rs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function up(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function save() {
    if (form.employee_count && Number(form.employee_count) < 1) {
      toast.error('Employee count must be 1 or greater.');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.employee_count !== '' && payload.employee_count != null) {
        payload.employee_count = Number(payload.employee_count);
      }
      await api.patch(`/engagements/${eng.id}`, payload);
      toast.success('Saved');
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function archive() {
    if (eng.status !== 'delivered') {
      toast.error('Mark the engagement as Delivered first, then archive.');
      return;
    }
    if (
      !window.confirm(
        'Archive this engagement?\n\n• It will move out of your active dashboard into the Archived tab.\n• All findings, photos, and reports stay in storage.\n• The client share link KEEPS working.\n\nYou can unarchive at any time.'
      )
    )
      return;
    try {
      await api.patch(`/engagements/${eng.id}/archive`);
      toast.success('Archived — client link still active');
      nav('/');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function unarchive() {
    try {
      await api.patch(`/engagements/${eng.id}/unarchive`);
      toast.success('Restored to Delivered');
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
      <div className="md:col-span-2">
        <div className="gl-label">Engagement Type</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { key: 'walkthrough', label: 'Gemba Walk' },
            { key: 'gap_check', label: 'Doc Review Only' },
            { key: 'combined', label: 'Combined' },
          ].map((t) => {
            const active = eng.type === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={async () => {
                  if (t.key === eng.type) return;
                  if (!window.confirm(`Switch this engagement to ${t.label}?\n\nYour facility info, findings, and any existing checklist data are kept. New checklist items are added automatically if needed.`)) return;
                  try {
                    await api.patch(`/engagements/${eng.id}`, { type: t.key });
                    toast.success(`Switched to ${t.label}`);
                    refresh();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  }
                }}
                className="text-left p-3 border-2 rounded-sm"
                style={{
                  background: active ? 'var(--gl-dark)' : '#fff',
                  color: active ? '#fff' : 'var(--gl-dark)',
                  borderColor: active ? 'var(--gl-dark)' : '#e5e7eb',
                }}
                data-testid={`setup-type-${t.key}`}
              >
                <div className="font-display font-extrabold uppercase tracking-wide">
                  {t.label}
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-xs mt-2" style={{ color: 'var(--gl-muted)' }}>
          Switch anytime. No data is deleted — just adds checklist items when needed.
        </div>
      </div>
      <F label="Business Name">
        <input className="gl-input" value={form.business_name} onChange={(e) => up('business_name', e.target.value)} data-testid="setup-business-name" />
      </F>
      <F label="Industry">
        <input className="gl-input" value={form.industry} onChange={(e) => up('industry', e.target.value)} />
      </F>
      <F label="Contact Name">
        <input className="gl-input" value={form.contact_name} onChange={(e) => up('contact_name', e.target.value)} />
      </F>
      <F label="Contact Email">
        <input className="gl-input" type="email" value={form.contact_email} onChange={(e) => up('contact_email', e.target.value)} />
      </F>
      <F label="Contact Phone">
        <input className="gl-input" value={form.contact_phone} onChange={(e) => up('contact_phone', e.target.value)} />
      </F>
      <F label="Visit Date">
        <input className="gl-input" type="date" value={form.visit_date} onChange={(e) => up('visit_date', e.target.value)} />
      </F>
      <ThresholdFields
        employeeCount={form.employee_count}
        industryType={form.industry_type}
        onChange={up}
      />
      <div className="md:col-span-2">
        <F label="Site Address">
          <input className="gl-input" value={form.site_address} onChange={(e) => up('site_address', e.target.value)} />
        </F>
      </div>
      <div className="md:col-span-2">
        <F label="Address Note (optional)">
          <input
            className="gl-input"
            value={form.site_address_note}
            onChange={(e) => up('site_address_note', e.target.value)}
            placeholder="Gate code 4592, use back entrance"
            data-testid="eng-setup-site-address-note"
          />
        </F>
      </div>
      <div className="md:col-span-2">
        <F label="Notes">
          <textarea className="gl-input gl-textarea" rows={3} value={form.notes} onChange={(e) => up('notes', e.target.value)} />
        </F>
      </div>
      {/* GL-SPEC-APP-004 — Facility Hazard Flags (drives Document Coverage rules) */}
      <div className="md:col-span-2">
        <div className="gl-label">Facility Hazard Profile</div>
        <div
          className="p-3 border-2 rounded-sm"
          style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3' }}
        >
          <div
            className="text-xs uppercase tracking-wider mb-3"
            style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
          >
            Toggle any condition present at this facility. These drive which OSHA
            documents are required in the Docs tab.
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {HAZARD_FLAGS.map((h) => (
              <HazardFlag
                key={h.key}
                hazard={h}
                checked={!!form[h.key]}
                onChange={(v) => up(h.key, v)}
              />
            ))}
          </div>
        </div>
      </div>
      {(eng.type === 'gap_check' || eng.type === 'combined') && (
        <div className="md:col-span-2">
          <div className="gl-label">Commercial Motor Vehicle Operations</div>
          <label
            className="flex items-start gap-3 p-3 border-2 rounded-sm cursor-pointer"
            style={{
              borderColor: form.has_fleet ? 'var(--gl-navy)' : '#d1d5db',
              background: form.has_fleet ? '#eff4fb' : '#fff',
            }}
          >
            <input
              type="checkbox"
              className="mt-1 w-5 h-5"
              checked={form.has_fleet}
              onChange={(e) => up('has_fleet', e.target.checked)}
              data-testid="setup-has-fleet"
            />
            <div>
              <div className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.06em' }}>
                Facility operates commercial motor vehicles (FMCSA)
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
                When on, the 9-item Fleet / FMCSA category appears in the checklist and counts toward the score. Turning it off removes those items.
              </div>
            </div>
          </label>
        </div>
      )}
      <div className="md:col-span-2 flex gap-3 mt-3 flex-wrap">
        <button className="gl-btn gl-btn-primary" onClick={save} disabled={busy} data-testid="setup-save">
          <FloppyDiskIcon size={20} weight="bold" /> {busy ? 'Saving…' : 'Save Changes'}
        </button>
        <div className="ml-auto flex gap-2 flex-wrap items-center">
          {eng.status === 'archived' ? (
            <button className="gl-btn gl-btn-accent" onClick={unarchive} data-testid="setup-unarchive">
              <ArrowCounterClockwiseIcon size={20} weight="bold" /> Restore to Delivered
            </button>
          ) : (
            <>
              {eng.status !== 'delivered' && (
                <span className="text-xs uppercase tracking-widest" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.1em' }}>
                  Mark Delivered first to enable Archive
                </span>
              )}
              <button
                className="gl-btn gl-btn-secondary"
                onClick={archive}
                disabled={eng.status !== 'delivered'}
                data-testid="setup-archive"
                title={eng.status !== 'delivered' ? 'Archive available after engagement is marked Delivered' : 'Archive engagement'}
              >
                <ArchiveIcon size={20} weight="bold" /> Archive
              </button>
            </>
          )}
        </div>
      </div>

      {/* Reminder calendar */}
      {(eng.status === 'delivered' || eng.status === 'archived') && (
        <div className="md:col-span-2 mt-4 gl-card gl-card-accent" style={{ borderLeftColor: 'var(--gl-gold)' }}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <BellRingingIcon size={22} weight="fill" color="var(--gl-navy)" />
              <span className="font-display font-extrabold uppercase text-xl" style={{ color: 'var(--gl-dark)' }}>
                Compliance Reminders
              </span>
              {reminders && (
                <span className="gl-chip" style={{ background: 'var(--gl-navy)', color: '#fff' }}>
                  {reminders.length} scheduled
                </span>
              )}
            </div>
            <Link to="/reminders" className="gl-btn gl-btn-secondary !h-9 !px-3 !text-xs">
              All Reminders <ArrowRightIcon size={14} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 mb-3 p-3 border-2 rounded-sm" style={{ background: '#fdf6e3', borderColor: 'var(--gl-gold)' }}>
            <div>
              <div className="gl-label" style={{ color: 'var(--gl-dark)' }}>Client Notification Email</div>
              <input
                className="gl-input"
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
                placeholder="client@example.com"
                data-testid="setup-reminder-client-email"
              />
              <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
                If client toggle is on, reminders go to this address in addition to Vince.
              </div>
            </div>
            <div className="flex flex-col gap-2 md:items-end justify-end">
              <button className="gl-btn gl-btn-accent !h-10 !px-3 !text-xs" onClick={() => toggleAllNotify(true)} data-testid="setup-notify-client-on">
                Notify Client: ON
              </button>
              <button className="gl-btn gl-btn-secondary !h-10 !px-3 !text-xs" onClick={() => toggleAllNotify(false)} data-testid="setup-notify-client-off">
                Notify Client: OFF
              </button>
            </div>
          </div>

          {reminders === null ? (
            <div className="text-sm" style={{ color: 'var(--gl-muted)' }}>Loading…</div>
          ) : reminders.length === 0 ? (
            <div className="text-sm" style={{ color: 'var(--gl-muted)' }}>No reminders generated for this engagement.</div>
          ) : (
            <div className="space-y-1.5">
              {reminders.slice(0, 8).map((r) => (
                <div key={r.id} className="flex items-center gap-3 text-sm py-1 border-b border-gray-100 last:border-0">
                  <span className="font-bold w-28 shrink-0" style={{ color: r.status === 'handled' ? 'var(--gl-p3)' : r.status === 'sent' ? 'var(--gl-gold)' : 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.05em' }}>
                    {r.reminder_date}
                  </span>
                  <span className="flex-1" style={{ color: r.status === 'handled' ? '#9ca3af' : 'var(--gl-dark)', textDecoration: r.status === 'handled' ? 'line-through' : 'none' }}>
                    {r.item_label}
                  </span>
                  <span className="text-xs uppercase tracking-wider font-bold" style={{ color: r.notify_client ? 'var(--gl-navy)' : 'var(--gl-muted)', fontFamily: 'Inter' }}>
                    {r.notify_client ? 'Vince + Client' : 'Vince only'}
                  </span>
                </div>
              ))}
              {reminders.length > 8 && (
                <div className="text-xs pt-1" style={{ color: 'var(--gl-muted)' }}>+{reminders.length - 8} more — open Reminders tab</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="gl-label">{label}</div>
      {children}
    </div>
  );
}
